// src/utils/differenceTexte.ts — SCE Issues
// Ce qui a changé d'une version à l'autre d'un texte (29/10) : l'historique d'un
// titre, d'une description ou d'un commentaire barrait toute l'ancienne version,
// même pour un mot corrigé. On compare donc MOT À MOT, comme le suivi des
// modifications de Word : mots retirés barrés, mots ajoutés marqués, le reste
// tel quel.
//
// Règles, pour que le résultat se lise d'un coup d'œil :
//   - un « mot » garde ses traits d'union, points, barres : H-PT-1010,
//     NVS50312-G, 12/14 et l'article restent d'un bloc ;
//   - deux changements séparés d'une espace ou d'un mot court (« de », « à »)
//     n'en font qu'un : « Capteur de pression » → « H-PT-1010 » se lit comme
//     un remplacement, pas comme trois morceaux entrelacés ;
//   - une faute corrigée dans un mot (« défaillnt » → « défaillant ») est
//     marquée à la LETTRE, si le mot est assez long et reste reconnaissable ;
//   - un LONG texte réécrit presque entièrement n'est pas découpé : on dit
//     « Réécrit » et on montre le nouveau texte (un titre court, lui, se lit
//     très bien en ancien barré + nouveau marqué).
// Pur : aucun React, aucun réseau (tests/differenceTexte.test.ts).

export type Morceau =
  | { type: 'egal'; texte: string }
  | { type: 'retrait'; texte: string }
  | { type: 'ajout'; texte: string }

export interface Difference {
  morceaux: Morceau[]
  /** Presque tout a changé : afficher le nouveau texte, sans marques. */
  reecrit: boolean
  /** « 1 mot remplacé », « 3 mots ajoutés · 1 retiré », « Réécrit »… */
  resume: string
}

/** Mot (lettres, chiffres, liés par - ' ’ . / , _), espaces, ou signe seul. */
const JETON = /\s+|[\p{L}\p{N}]+(?:[-'’./,_][\p{L}\p{N}]+)*|[^\s\p{L}\p{N}]/gu
const EST_MOT = /[\p{L}\p{N}]/u

/** Au-delà, la comparaison complète coûterait trop : le milieu est remplacé d'un bloc. */
const CASES_MAX = 2_500_000
/** Part du texte restée identique sous laquelle on parle de réécriture. */
const SEUIL_REECRIT = 0.25
/** Longueur (hors espaces) en dessous de laquelle on ne parle jamais de réécriture. */
const LONGUEUR_REECRIT = 80

function jetons(texte: string): string[] {
  return texte.match(JETON) ?? []
}

function nbMots(texte: string): number {
  return jetons(texte).filter((j) => EST_MOT.test(j)).length
}

/** Plus longue sous-suite commune des jetons, rendue en morceaux. */
function comparerJetons(a: readonly string[], b: readonly string[]): Morceau[] {
  const n = a.length
  const m = b.length
  if (n === 0) return m === 0 ? [] : [{ type: 'ajout', texte: b.join('') }]
  if (m === 0) return [{ type: 'retrait', texte: a.join('') }]
  if ((n + 1) * (m + 1) > CASES_MAX) {
    return [
      { type: 'retrait', texte: a.join('') },
      { type: 'ajout', texte: b.join('') },
    ]
  }
  // long[i][j] = sous-suite commune de a[i..] et b[j..], à plat.
  const largeur = m + 1
  const long = new Uint32Array((n + 1) * largeur)
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      long[i * largeur + j] =
        a[i] === b[j]
          ? long[(i + 1) * largeur + j + 1]! + 1
          : Math.max(long[(i + 1) * largeur + j]!, long[i * largeur + j + 1]!)
    }
  }
  const sortie: Morceau[] = []
  let i = 0
  let j = 0
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      sortie.push({ type: 'egal', texte: a[i]! })
      i++
      j++
    } else if (long[(i + 1) * largeur + j]! >= long[i * largeur + j + 1]!) {
      sortie.push({ type: 'retrait', texte: a[i]! })
      i++
    } else {
      sortie.push({ type: 'ajout', texte: b[j]! })
      j++
    }
  }
  while (i < n) sortie.push({ type: 'retrait', texte: a[i++]! })
  while (j < m) sortie.push({ type: 'ajout', texte: b[j++]! })
  return sortie
}

/** Un changement : ce qui part, ce qui arrive (l'un des deux peut être vide). */
interface Bloc {
  retrait: string
  ajout: string
}

type Element = { egal: string } | Bloc

const estBloc = (e: Element): e is Bloc => !('egal' in e)

/** Regroupe les morceaux en « texte égal » et « changement ». */
function enElements(morceaux: readonly Morceau[]): Element[] {
  const elements: Element[] = []
  for (const mc of morceaux) {
    const dernier = elements[elements.length - 1]
    if (mc.type === 'egal') {
      if (dernier && !estBloc(dernier)) dernier.egal += mc.texte
      else elements.push({ egal: mc.texte })
    } else {
      const bloc = dernier && estBloc(dernier) ? dernier : null
      const cible = bloc ?? { retrait: '', ajout: '' }
      if (!bloc) elements.push(cible)
      if (mc.type === 'retrait') cible.retrait += mc.texte
      else cible.ajout += mc.texte
    }
  }
  return elements
}

/**
 * Un texte égal coincé entre deux changements, fait d'espaces ou d'un mot
 * court, rejoint les deux : un seul changement à lire au lieu de trois.
 */
function absorberPetitsEgaux(elements: Element[]): Element[] {
  let change = true
  while (change) {
    change = false
    for (let k = 1; k + 1 < elements.length; k++) {
      const avant = elements[k - 1]!
      const milieu = elements[k]!
      const apres = elements[k + 1]!
      if (!estBloc(avant) || estBloc(milieu) || !estBloc(apres)) continue
      const e = milieu.egal
      const court = e.trim() === '' || (!e.includes('\n') && e.trim().length <= 3)
      if (!court) continue
      elements.splice(k - 1, 3, { retrait: avant.retrait + e + apres.retrait, ajout: avant.ajout + e + apres.ajout })
      change = true
      break
    }
  }
  return elements
}

/**
 * Un mot remplacé par un mot proche (faute corrigée) : marqué à la lettre.
 * Mots d'au moins 4 lettres, 60 % du plus long en commun (début + fin).
 */
function affinerALaLettre(bloc: Bloc): Morceau[] | null {
  const { retrait, ajout } = bloc
  if (!retrait || !ajout || /\s/.test(retrait) || /\s/.test(ajout)) return null
  // Une référence (V12, 12/14, NVS50312-G) change d'un bloc : « 1[4]{5} » ne se lit pas.
  if (/\p{N}/u.test(retrait) || /\p{N}/u.test(ajout)) return null
  if (Math.min(retrait.length, ajout.length) < 4) return null
  let debut = 0
  while (debut < retrait.length && debut < ajout.length && retrait[debut] === ajout[debut]) debut++
  let fin = 0
  while (
    fin < retrait.length - debut &&
    fin < ajout.length - debut &&
    retrait[retrait.length - 1 - fin] === ajout[ajout.length - 1 - fin]
  ) {
    fin++
  }
  if (debut + fin < 0.6 * Math.max(retrait.length, ajout.length)) return null
  const morceaux: Morceau[] = []
  if (debut > 0) morceaux.push({ type: 'egal', texte: retrait.slice(0, debut) })
  const parti = retrait.slice(debut, retrait.length - fin)
  const arrive = ajout.slice(debut, ajout.length - fin)
  if (parti) morceaux.push({ type: 'retrait', texte: parti })
  if (arrive) morceaux.push({ type: 'ajout', texte: arrive })
  if (fin > 0) morceaux.push({ type: 'egal', texte: retrait.slice(retrait.length - fin) })
  return morceaux
}

function pluriel(n: number, mot: string): string {
  return `${n} ${mot}${n > 1 ? 's' : ''}`
}

function resumer(blocs: readonly Bloc[]): string {
  let remplaces = 0
  let ajoutes = 0
  let retires = 0
  for (const b of blocs) {
    const a = nbMots(b.ajout)
    const r = nbMots(b.retrait)
    if (a > 0 && r > 0) remplaces += Math.max(a, r)
    else if (a > 0) ajoutes += a
    else if (r > 0) retires += r
  }
  if (remplaces + ajoutes + retires === 0) return 'Mise en forme'
  const parties: string[] = []
  // Le premier porte le mot « mot », les suivants s'en passent.
  const nommer = (n: number, verbe: string): string =>
    parties.length === 0 ? `${pluriel(n, 'mot')} ${verbe}${n > 1 ? 's' : ''}` : `${n} ${verbe}${n > 1 ? 's' : ''}`
  if (remplaces > 0) parties.push(nommer(remplaces, 'remplacé'))
  if (ajoutes > 0) parties.push(nommer(ajoutes, 'ajouté'))
  if (retires > 0) parties.push(nommer(retires, 'retiré'))
  return parties.join(' · ')
}

/** Compare deux versions d'un texte : `avant` la plus ancienne, `apres` la suivante. */
export function comparerTextes(avant: string, apres: string): Difference {
  if (avant === apres) return { morceaux: apres ? [{ type: 'egal', texte: apres }] : [], reecrit: false, resume: 'Inchangé' }

  const a = jetons(avant)
  const b = jetons(apres)
  // Début et fin communs mis de côté : la comparaison ne porte que sur le milieu.
  let debut = 0
  while (debut < a.length && debut < b.length && a[debut] === b[debut]) debut++
  let fin = 0
  while (fin < a.length - debut && fin < b.length - debut && a[a.length - 1 - fin] === b[b.length - 1 - fin]) fin++

  const morceaux: Morceau[] = []
  if (debut > 0) morceaux.push({ type: 'egal', texte: a.slice(0, debut).join('') })
  morceaux.push(...comparerJetons(a.slice(debut, a.length - fin), b.slice(debut, b.length - fin)))
  if (fin > 0) morceaux.push({ type: 'egal', texte: a.slice(a.length - fin).join('') })

  const elements = absorberPetitsEgaux(enElements(morceaux))
  const blocs = elements.filter(estBloc)

  // Part restée identique (hors espaces), rapportée au plus long des deux textes.
  const egal = elements.reduce((s, e) => s + (estBloc(e) ? 0 : e.egal.replace(/\s/g, '').length), 0)
  const plusLong = Math.max(avant.replace(/\s/g, '').length, apres.replace(/\s/g, '').length)
  // Un texte court (un titre) se lit très bien en « [ancien]{nouveau} » : seul un
  // long texte réécrit est montré sans marques.
  const reecrit = avant.trim() !== '' && plusLong > LONGUEUR_REECRIT && egal / plusLong < SEUIL_REECRIT

  if (reecrit) return { morceaux: [{ type: 'egal', texte: apres }], reecrit: true, resume: 'Réécrit' }

  const sortie: Morceau[] = []
  for (const e of elements) {
    if (!estBloc(e)) {
      sortie.push({ type: 'egal', texte: e.egal })
      continue
    }
    const fin = affinerALaLettre(e)
    if (fin) {
      sortie.push(...fin)
      continue
    }
    // Des espaces seuls retirés ne se voient pas barrés : on les tait.
    if (e.retrait.trim() !== '') sortie.push({ type: 'retrait', texte: e.retrait })
    if (e.ajout) sortie.push({ type: 'ajout', texte: e.ajout })
  }
  return { morceaux: fusionnerVoisins(sortie), reecrit: false, resume: resumer(blocs) }
}

function fusionnerVoisins(morceaux: readonly Morceau[]): Morceau[] {
  const sortie: Morceau[] = []
  for (const mc of morceaux) {
    const dernier = sortie[sortie.length - 1]
    if (dernier && dernier.type === mc.type) dernier.texte += mc.texte
    else sortie.push({ ...mc })
  }
  return sortie
}

/** Longueur au-delà de laquelle un passage inchangé est replié. */
export const EGAL_A_REPLIER = 240
/** Ce qu'on garde de part et d'autre d'un passage replié. */
const CONTEXTE = 80

/**
 * Un long passage inchangé, replié autour des changements : on garde la fin
 * de ce qui précède et le début de ce qui suit, coupés à un mot entier.
 * `null` : rien à replier.
 */
export function replierEgal(
  texte: string,
  position: 'debut' | 'milieu' | 'fin',
): { avant: string; apres: string } | null {
  if (texte.length <= EGAL_A_REPLIER) return null
  const tete = (s: string): string => {
    const coupe = s.slice(0, CONTEXTE)
    const espace = coupe.lastIndexOf(' ')
    return espace > CONTEXTE / 2 ? coupe.slice(0, espace + 1) : coupe
  }
  const queue = (s: string): string => {
    const coupe = s.slice(-CONTEXTE)
    const espace = coupe.indexOf(' ')
    return espace >= 0 && espace < CONTEXTE / 2 ? coupe.slice(espace) : coupe
  }
  if (position === 'debut') return { avant: '', apres: queue(texte) }
  if (position === 'fin') return { avant: tete(texte), apres: '' }
  return { avant: tete(texte), apres: queue(texte) }
}
